import React from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * Skeleton complet pour la tab de génération de code SILAE
 * Inclut le StepIndicator, le contenu du formulaire et le footer
 */
export const SilaeTabSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col min-h-full">
      {/* Header avec Step Indicator Skeleton */}
      <div className="flex-shrink-0 space-y-6">
        {/* Step Indicator */}
        <div className="flex items-center justify-between gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center flex-1">
                <div className="flex items-center w-full">
                  {/* Cercle de l'étape avec icône */}
                  <div className="flex flex-col items-center relative z-10">
                    <div className="relative">
                      <Skeleton
                        variant="circular"
                        className="h-12 w-12"
                        style={{ animationDelay: `${i * 100}ms` }}
                      />
                      <Skeleton
                        variant="rectangular"
                        className="h-5 w-5 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                        style={{ animationDelay: `${i * 100 + 50}ms` }}
                      />
                    </div>
                    <Skeleton
                      variant="text"
                      className="h-4 w-24 mt-2 text-center"
                      style={{ animationDelay: `${i * 100 + 100}ms` }}
                    />
                  </div>
                  {/* Ligne de connexion */}
                  {i < 3 && (
                    <Skeleton
                      variant="rectangular"
                      className="h-0.5 flex-1 mx-2 -mt-6"
                      style={{ animationDelay: `${i * 100 + 25}ms` }}
                    />
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}
          {/* Bouton Réinitialiser */}
          <Skeleton
            variant="rectangular"
            className="h-9 w-32 rounded-md ml-auto"
            style={{ animationDelay: "400ms" }}
          />
        </div>
      </div>

      {/* Body scrollable avec contenu du formulaire */}
      <div className="flex-1 overflow-y-auto">
        <Card className="space-y-6 p-4 gap-4 rounded-3xl">
          {/* Header avec titre et bouton */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Skeleton
                variant="rectangular"
                className="h-5 w-5"
                style={{ animationDelay: "200ms" }}
              />
              <Skeleton
                variant="text"
                className="h-6 w-48"
                style={{ animationDelay: "250ms" }}
              />
            </div>
            <Skeleton
              variant="rectangular"
              className="h-9 w-40 rounded-md"
              style={{ animationDelay: "300ms" }}
            />
          </div>

          {/* Tabs pour les primes */}
          <Tabs value="bonus-0">
            <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
              <TabsTrigger value="bonus-0" className="flex items-center gap-2">
                <Skeleton
                  variant="circular"
                  className="h-3 w-3"
                  style={{ animationDelay: "350ms" }}
                />
                <Skeleton
                  variant="text"
                  className="h-4 w-20"
                  style={{ animationDelay: "400ms" }}
                />
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Contenu du formulaire */}
          <div className="flex flex-col gap-4">
            {/* Conditions de la prime */}
            <Card className="bg-background dark:bg-background">
              <div className="flex mb-0 items-center p-4 mx-6 rounded-lg bg-muted/50 justify-between">
                <div className="flex flex-col justify-between flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Skeleton
                      variant="rectangular"
                      className="h-5 w-5"
                      style={{ animationDelay: "450ms" }}
                    />
                    <Skeleton
                      variant="text"
                      className="h-5 w-48"
                      style={{ animationDelay: "500ms" }}
                    />
                  </div>
                  <Skeleton
                    variant="text"
                    className="h-4 w-full max-w-md"
                    style={{ animationDelay: "550ms" }}
                  />
                </div>
                <Skeleton
                  variant="rectangular"
                  className="h-6 w-11 rounded-full"
                  style={{ animationDelay: "600ms" }}
                />
              </div>
            </Card>

            {/* Configuration des paramètres de base */}
            <Card className="bg-background dark:bg-background mb-0">
              <div className="p-6">
                <Skeleton
                  variant="text"
                  className="h-6 w-64 mb-2"
                  style={{ animationDelay: "650ms" }}
                />
                <Skeleton
                  variant="text"
                  className="h-4 w-96 mb-4"
                  style={{ animationDelay: "700ms" }}
                />
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Code de la rubrique */}
                  <div className="space-y-2 flex-1">
                    <Skeleton
                      variant="text"
                      className="h-4 w-40"
                      style={{ animationDelay: "750ms" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      className="h-10 w-full rounded-md"
                      style={{ animationDelay: "800ms" }}
                    />
                  </div>
                  {/* Méthode de calcul */}
                  <div className="space-y-2 flex-1">
                    <Skeleton
                      variant="text"
                      className="h-4 w-32"
                      style={{ animationDelay: "850ms" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      className="h-10 w-full rounded-md"
                      style={{ animationDelay: "900ms" }}
                    />
                  </div>
                  {/* Libellé */}
                  <div className="space-y-2 flex-1">
                    <Skeleton
                      variant="text"
                      className="h-4 w-32"
                      style={{ animationDelay: "950ms" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      className="h-10 w-full rounded-md"
                      style={{ animationDelay: "1000ms" }}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Source de la Base */}
            <Card className="bg-background dark:bg-background mb-0">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <Skeleton
                      variant="text"
                      className="h-6 w-48 mb-2"
                      style={{ animationDelay: "1050ms" }}
                    />
                    <Skeleton
                      variant="text"
                      className="h-4 w-full max-w-lg"
                      style={{ animationDelay: "1100ms" }}
                    />
                  </div>
                  <Skeleton
                    variant="rectangular"
                    className="h-9 w-36 rounded-md"
                    style={{ animationDelay: "1150ms" }}
                  />
                </div>
                <div className="space-y-4">
                  {/* Conditions de la source */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2 flex-1">
                      <Skeleton
                        variant="rectangular"
                        className="h-5 w-5"
                        style={{ animationDelay: "1200ms" }}
                      />
                      <div className="flex-1">
                        <Skeleton
                          variant="text"
                          className="h-5 w-40 mb-2"
                          style={{ animationDelay: "1250ms" }}
                        />
                        <Skeleton
                          variant="text"
                          className="h-4 w-full max-w-md"
                          style={{ animationDelay: "1300ms" }}
                        />
                      </div>
                    </div>
                    <Skeleton
                      variant="rectangular"
                      className="h-6 w-11 rounded-full"
                      style={{ animationDelay: "1350ms" }}
                    />
                  </div>

                  {/* Source de la base selection */}
                  <div className="space-y-3">
                    <Skeleton
                      variant="text"
                      className="h-4 w-32"
                      style={{ animationDelay: "1400ms" }}
                    />
                    <div className="flex gap-2">
                      <Skeleton
                        variant="rectangular"
                        className="h-10 w-48 rounded-md"
                        style={{ animationDelay: "1450ms" }}
                      />
                      <Skeleton
                        variant="rectangular"
                        className="h-10 w-32 rounded-md"
                        style={{ animationDelay: "1500ms" }}
                      />
                    </div>
                  </div>

                  {/* Variable SILAE Avancée */}
                  <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                    <Skeleton
                      variant="text"
                      className="h-5 w-48"
                      style={{ animationDelay: "1550ms" }}
                    />
                    <Skeleton
                      variant="text"
                      className="h-4 w-full max-w-lg"
                      style={{ animationDelay: "1600ms" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      className="h-10 w-full rounded-md"
                      style={{ animationDelay: "1650ms" }}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Permettre le forçage via EVP */}
            <Card className="bg-background dark:bg-background">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Skeleton
                      variant="text"
                      className="h-5 w-48 mb-2"
                      style={{ animationDelay: "1700ms" }}
                    />
                    <Skeleton
                      variant="text"
                      className="h-4 w-full max-w-2xl"
                      style={{ animationDelay: "1750ms" }}
                    />
                  </div>
                  <Skeleton
                    variant="rectangular"
                    className="h-6 w-11 rounded-full"
                    style={{ animationDelay: "1800ms" }}
                  />
                </div>
              </div>
            </Card>

            {/* Sections avec toggles en bas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Stockage */}
              <Card className="bg-background dark:bg-background">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Skeleton
                        variant="rectangular"
                        className="h-5 w-5"
                        style={{ animationDelay: "1850ms" }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Skeleton
                            variant="text"
                            className="h-5 w-40"
                            style={{ animationDelay: "1900ms" }}
                          />
                          <Skeleton
                            variant="rectangular"
                            className="h-5 w-16 rounded-full"
                            style={{ animationDelay: "1950ms" }}
                          />
                        </div>
                        <Skeleton
                          variant="text"
                          className="h-4 w-full max-w-sm"
                          style={{ animationDelay: "2000ms" }}
                        />
                      </div>
                    </div>
                    <Skeleton
                      variant="rectangular"
                      className="h-6 w-11 rounded-full"
                      style={{ animationDelay: "2050ms" }}
                    />
                  </div>
                </div>
              </Card>

              {/* Compteurs */}
              <Card className="bg-background dark:bg-background">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Skeleton
                        variant="rectangular"
                        className="h-5 w-5"
                        style={{ animationDelay: "2100ms" }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Skeleton
                            variant="text"
                            className="h-5 w-36"
                            style={{ animationDelay: "2150ms" }}
                          />
                          <Skeleton
                            variant="rectangular"
                            className="h-5 w-16 rounded-full"
                            style={{ animationDelay: "2200ms" }}
                          />
                        </div>
                        <Skeleton
                          variant="text"
                          className="h-4 w-full max-w-sm"
                          style={{ animationDelay: "2250ms" }}
                        />
                      </div>
                    </div>
                    <Skeleton
                      variant="rectangular"
                      className="h-6 w-11 rounded-full"
                      style={{ animationDelay: "2300ms" }}
                    />
                  </div>
                </div>
              </Card>
            </div>

            {/* Mémos */}
            <Card className="bg-background dark:bg-background">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Skeleton
                      variant="rectangular"
                      className="h-5 w-5"
                      style={{ animationDelay: "2350ms" }}
                    />
                    <Skeleton
                      variant="text"
                      className="h-5 w-40"
                      style={{ animationDelay: "2400ms" }}
                    />
                  </div>
                  <Skeleton
                    variant="rectangular"
                    className="h-6 w-11 rounded-full"
                    style={{ animationDelay: "2450ms" }}
                  />
                </div>
                <Skeleton
                  variant="rectangular"
                  className="h-24 w-full rounded-md"
                  style={{ animationDelay: "2500ms" }}
                />
              </div>
            </Card>
          </div>
        </Card>
      </div>

      {/* Footer avec navigation */}
      <div className="flex-shrink-0 flex items-center justify-between py-6">
        {/* Bouton Précédent */}
        <Skeleton
          variant="rectangular"
          className="h-10 w-32 rounded-md"
          style={{ animationDelay: "2550ms" }}
        />

        {/* Informations centrales */}
        <div className="flex items-center gap-2">
          <Skeleton
            variant="text"
            className="h-4 w-24"
            style={{ animationDelay: "2600ms" }}
          />
          <Skeleton
            variant="text"
            className="h-4 w-1"
            style={{ animationDelay: "2650ms" }}
          />
          <Skeleton
            variant="rectangular"
            className="h-5 w-20 rounded-full"
            style={{ animationDelay: "2700ms" }}
          />
        </div>

        {/* Bouton Suivant */}
        <Skeleton
          variant="rectangular"
          className="h-10 w-32 rounded-md"
          style={{ animationDelay: "2750ms" }}
        />
      </div>
    </div>
  );
};
